import { type ThunkAction } from "redux-thunk";
import { type FetchWishlistsResponse } from "../api/fetchWishlists";
import { type AddToCartItem, type AddToCartPartItem, sendBulkAddToCart } from "~/feature/cart/cart/api/AddToCart";
import { sendData as sendDataWishlist } from "../api/AddToWishlist";
import { sendData as sendAddCartToWishlist } from "../api/AddCartToWishlist";
import { sendData as removeWishlistProduct } from "../api/RemoveFromWishlist";
import { sendData as removeWishlistApi } from "../api/RemoveWishlist";
import { sendData as renameWishlistApi } from "../api/RenameWishlist";
import { sendData as createWishlistApi } from "../api/CreateWishlist";
import * as PriceUtils from "../../utils/PriceUtils";
import { INVALID_PACKAGE_CODE } from "../../utils/constants";
import { type AnyAction } from "../../redux/types";
import { addMessage, addMessageAdvanced } from "../../redux/globalMessages/actions";
import { addQuantity } from "../../AddToCart/redux/cartAmounts/actions";
import { addBaseProductDataAction, requestAvailabilityData, requestPriceData } from "../../redux/products/actions";
import debounce from "lodash/debounce";
import { selectProperAmountInCart } from "../../AddToCart/redux/cartAmounts/cartAmountSelectors";
import { batch } from "react-redux";
import { selectProductData, selectProductDescriptor } from "../../redux/products/productsSelectors";
import { bulkAddToWishlist } from "../api/BulkAddToWishlist";
import type { RootState } from "../../redux/store";
import {
  convertProductToImpression,
  sendWishlistAddSelectedToCartClick,
  sendWishlistProductListImpression,
} from "../../feature/Analytics/ga/ga";
import { sendUpdateWishlistEntry } from "../api/UpdateWishlistEntry";
import { isKITProduct, type KITProductData, type ProductData } from "../../models/ProductData";
import {
  type ExpandedResolvedWishlistEntryModel,
  type ResolvedWishlistEntryModel,
  type WishlistEntryModel,
} from "../model";
import { userIsCustomer } from "../../utils/roles";
import {
  getNetPrice,
  getPackagingInformation,
  getSurcharge,
  getTariff,
  isProductPMA,
  selectNetPrice,
} from "../../feature/ProductDescriptor/v1/productDescriptorUtils";
import { type OrderLineItemData } from "../../OrdersQuotes/common/modelsV2";
import { calculateSalesCampaign4PDP } from "~/SalesCampaign/utils";
import { ProductService } from "~/AddToCart/ProductService";
import { makeSalesAwarePackCodeProvider } from "~/SalesCampaign/salesAwarePackCodeProvider";

import { getInitialPriceReference } from "~/AddToCart/redux/addToCart/getInitialPriceReference";

import { isNonNull } from "~/utils/reducers";
import { openModal } from "~/AddToCart/ModalSevice";
import CartSuccessModal from "~/AddToCart/components/AddToCartProcess/Modals/CartSuccessModal/CartSuccessModal";
import { TranslationType } from "~/common/TranslationModel";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { fetchWishlistNames, selectWishlistThunk } from "./thunks";
import {
  DELETE_PRODUCT,
  DELETE_WISHLIST,
  LOAD_WISHLIST_DETAILS_POST_PROCESS,
  LOAD_WISHLISTS_START,
  LOAD_WISHLISTS_SUCCESS,
  RENAME_WISHLIST,
  SAVE_COMMENT,
  SELECT_WISHLIST,
  SET_ADD_ALL_CLICKED,
  UPDATE_WISHLIST_ENTRY,
} from "./actionTypes";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

export function loadWishlistNamesStartAction() {
  return { type: LOAD_WISHLISTS_START } as const;
}

export function loadWishlistNamesSuccessAction(data: FetchWishlistsResponse) {
  return {
    type: LOAD_WISHLISTS_SUCCESS,
    payload: data,
  } as const;
}

export function setAddAllClicked(addAllClicked: boolean) {
  return {
    type: SET_ADD_ALL_CLICKED,
    payload: addAllClicked,
  } as const;
}

export function selectWishlistAction(wishlistId?: string) {
  return {
    type: SELECT_WISHLIST,
    payload: wishlistId,
  } as const;
}

export function loadWishlistDetailsPostProcessAction(wishlistId: string) {
  return {
    type: LOAD_WISHLIST_DETAILS_POST_PROCESS,
    payload: wishlistId,
  } as const;
}

export function saveCommentAction(wishlistId: string, comment: string) {
  return {
    type: SAVE_COMMENT,
    payload: {
      wishlistId,
      comment,
    },
  } as const;
}

export function removeSelectedWishlist(): ThunkResult<Promise<unknown>> {
  return (dispatch, getState) => {
    const selectedWishlistId = getState().wishlistPage.selectedWishlistId;
    const newSelectedWishlistId: string | undefined = getState().wishlistPage.wishlistIds.filter(
      (id) => id !== selectedWishlistId,
    )[0];

    const deletedWishlist = getState().wishlistPage.wishlists[selectedWishlistId!];

    return removeWishlistApi(selectedWishlistId!)
      .then(() => {
        return dispatch(selectWishlistThunk(newSelectedWishlistId));
      })
      .then(() =>
        dispatch({
          type: DELETE_WISHLIST,
          payload: {
            wishlistId: selectedWishlistId!,
          },
        }),
      )
      .then(() => {
        return dispatch(
          addMessageAdvanced("info", {
            type: TranslationType.INTERNAL,
            value: "wishlistPage.globalMsg.deleted",
            params: { name: deletedWishlist.name },
          }),
        );
      });
  };
}

export function removeProduct(entryId: string): ThunkResult<Promise<unknown>> {
  return (dispatch, getState) => {
    const state = getState().wishlistPage;
    const entry = state.entries[entryId];
    const wishlistId = state.selectedWishlistId!;

    return removeWishlistProduct({
      id: wishlistId,
      productCode: entry.productCode,
      wishlistEntryNumber: entry.wishlistEntryNumber,
    })
      .then(() =>
        dispatch({
          type: DELETE_PRODUCT,
          payload: {
            wishlistId: getState().wishlistPage.selectedWishlistId!,
            entryId,
          },
        }),
      )
      .then(() => dispatch(addMessage("info", "wishlistPage.globalMsg.applied", "translation")));
  };
}

// TODO: Think of a better way. Maybe debounced function should always return a promise? Rethink entire flow.
const debouncedSendUpdateWishlistEntry = debounce(sendUpdateWishlistEntry, 300);

function updateWishlistEntry(
  entryId: string,
  changes: Partial<WishlistEntryModel>,
  debounced?: boolean,
): ThunkResult<void> {
  return async (dispatch, getState) => {
    const state = getState().wishlistPage;
    const entry = state.entries[entryId];
    const wishlistId = state.selectedWishlistId!;

    const fn = debounced ? debouncedSendUpdateWishlistEntry : sendUpdateWishlistEntry;

    const updatedEntry = {
      ...entry,
      ...changes,
    };

    if (changes.productCode) {
      updatedEntry.available = true;
    }

    if (updatedEntry.quantity === 0) {
      // Sending 0 deletes the entry....
      updatedEntry.quantity = entry.quantity;
    }

    await fn({ wishlistId, oldEntry: entry, updatedEntry });

    dispatch({
      type: UPDATE_WISHLIST_ENTRY,
      payload: {
        updatedEntry,
        wishlistId,
      },
    });
  };
}

export function updateProduct(entryId: string, newProduct: ProductData): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState().wishlistPage;
    const entry = state.entries[entryId];

    dispatch(addBaseProductDataAction(newProduct));
    dispatch(requestPriceData(newProduct.code, entry.priceReference));
    dispatch(requestAvailabilityData(newProduct.code));

    dispatch(updateWishlistEntry(entryId, { productCode: newProduct.code }, true));
  };
}

export function updateQuantity(entryId: string, newQty?: number): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const entry = state.wishlistPage.entries[entryId];

    const product = selectProductData(entry.productCode)(state);
    let optionalParts = entry.optionalParts;

    if (newQty && isKITProduct(product)) {
      const findQty = (productCode: string) =>
        product.optionalParts.find((part) => part.product.code === productCode)?.quantity ?? 1;
      optionalParts = optionalParts.map((part) => ({ ...part, quantity: newQty! * findQty(part.productCode) }));
    }

    dispatch(updateWishlistEntry(entryId, { quantity: newQty, optionalParts }, true));
  };
}

export function updatePriceRef(entryId: string, newPriceRef: string): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState().wishlistPage;
    const entry = state.entries[entryId];

    dispatch(requestPriceData(entry.productCode, newPriceRef));

    dispatch(updateWishlistEntry(entryId, { priceReference: newPriceRef }, true));
  };
}

export function updateCommentItem(entryId: string, comment?: string): ThunkResult<void> {
  return updateWishlistEntry(entryId, { comment }, false);
}

function getATCApiEntry(
  entry: ResolvedWishlistEntryModel,
  state: RootState,
  packCode: string,
  saleEnabled: boolean,
): AddToCartItem {
  if (userIsCustomer()) {
    return getATCApiEntryCustomer(entry, state, packCode, saleEnabled);
  } else {
    return {
      productCode: entry.product.base.code,
      netPrice: 0,
      priceUnit: 1,
      priceReference: getInitialPriceReference(),
      quantity: entry.quantity,
      packageCode: INVALID_PACKAGE_CODE,
      packageSize: 1,
      surcharge: 0,
      tariffSurchargeApplicable: false,
      restrictedPackage: false,
      selectedDeliveryDate: null,
      selectedDeliveryDateIsManual: false,
      comment: entry.comment,
      primaryWarehouseCode: "",
      fifteenDigit: false,
      isPMA: isProductPMA(entry.product),
      originCountry: "",
    };
  }
}

function getATCApiEntryCustomer(
  entry: ResolvedWishlistEntryModel,
  state: RootState,
  packCode: string,
  saleEnabled: boolean,
): AddToCartItem {
  const quantity = entry.quantity ?? 0;

  const hasSales = calculateSalesCampaign4PDP({ productDescriptor: entry.product }).data?.hasSales;

  const packageSelector = makeSalesAwarePackCodeProvider(null);

  const productDescriptor = entry.product;

  const selectedPackCode = packCode;
  const selectedPackaging = productDescriptor.availability!.packagingInformation.find(
    (p) => p.packCode === selectedPackCode,
  )!;

  const packagingData = {
    primaryWarehouseCode: selectedPackaging?.primaryWarehouseCode ?? "N/A",
    packageSize: selectedPackaging?.packSize ?? 1,
    packageCode: selectedPackCode,
  };

  const baseData = {
    productCode: entry.product.base.code,
    selectedDeliveryDate: null,
    selectedDeliveryDateIsManual: false,
    quantity: quantity,
    comment: entry.comment,
    isPMA: isProductPMA(productDescriptor),
    restrictedPackage: false,
  };

  const qtyInCart =
    selectProperAmountInCart({
      productCode: productDescriptor.base.code,
      packCode: selectedPackCode,
      priceReference: entry.priceReference,
    })(state) ?? 0;

  const selectPriceProps = {
    totalQuantity: quantity + qtyInCart,
    packCode: selectedPackCode,
    product: productDescriptor,
  };
  const price = getNetPrice(selectPriceProps) ?? PriceUtils.zero();
  const surcharge = getSurcharge(selectPriceProps);
  const tariffSurcharge = getTariff(selectPriceProps.product);

  let salesData;
  if (hasSales && saleEnabled) {
    const salesDescriptor = selectProductDescriptor(state, {
      productCode: entry.product.base.code,
      priceReference: "",
    });
    const sales = calculateSalesCampaign4PDP({ productDescriptor: salesDescriptor }).data?.salePerPack ?? {};
    const saleForPack = sales[selectedPackCode];
    salesData = {
      salesCampaignCode: saleForPack.campaignCode,
      specialPrice: true,
      saleNetPrice: saleForPack?.actualPrice?.value,
    };
  } else {
    salesData = {
      specialPrice: false,
    };
  }

  let optionalParts: AddToCartPartItem[] | undefined = undefined;
  if (entry.optionalParts.length) {
    const parentDescriptor = productDescriptor as ProductDescriptor<KITProductData>;
    optionalParts = entry.optionalParts
      .map((part) => {
        const partDescriptor = selectProductDescriptor(state, {
          productCode: part.productCode,
          priceReference: entry.priceReference,
        });
        const partData = parentDescriptor.base.optionalParts.find(
          (partData) => partData.product.code === part.productCode,
        );
        if (!partData) {
          return undefined;
        }
        const partQty = entry.quantity * partData.quantity;
        const partPackageCode = packageSelector(partDescriptor, partQty);
        const partPackage = getPackagingInformation(partDescriptor, partPackageCode)!;

        const selectPriceProps = {
          productCode: part.productCode,
          priceReference: entry.priceReference,
          extraQuantity: partQty,
          packCode: partPackage?.packCode ?? "",
        };
        const price = selectNetPrice(selectPriceProps)(state);
        const fifteenDigit = partDescriptor.base.fifteenDigit[partPackage?.packCode ?? ""];

        return {
          productCode: part.productCode,
          quantity: partQty,
          netPrice: price?.value ?? 0,
          priceUnit: price?.unit ?? 1,
          primaryWarehouseCode: partPackage?.primaryWarehouseCode ?? "",
          packageCode: partPackage?.packCode ?? INVALID_PACKAGE_CODE,
          packageSize: partPackage?.packSize ?? 1,
          manualUpdated: true,
          partTypeCode: partData.type,
          surcharge: price.surcharge,
          tariffSurcharge: price.tariffSurcharge,
          originCountry: fifteenDigit?.countryOfOrigin ?? "",
        };
      })
      .filter(isNonNull);
  }
  const fifteenDigit = productDescriptor.base.fifteenDigit[selectedPackCode];

  return {
    ...baseData,
    ...packagingData,
    ...salesData,
    netPrice: price.value,
    priceUnit: price.unit,
    surcharge,
    tariffSurchargeApplicable: tariffSurcharge,
    fifteenDigit: !!productDescriptor.isMultiPrice,
    priceReference: productDescriptor.priceReference,
    optionalParts: optionalParts,
    originCountry: fifteenDigit?.countryOfOrigin ?? "",
  };
}

export function addToCartThunk(
  entry: ResolvedWishlistEntryModel,
  saleEnabled: boolean,
  packCode: string,
): ThunkResult<void> {
  return async (dispatch) => {
    await dispatch(bulkAddFromWishlistToCartThunk([{ entry, saleEnabled, packCode }]));
  };
}

export function addAllToCart(selectedEntries: ExpandedResolvedWishlistEntryModel[]): ThunkResult<void> {
  return async (dispatch, getState) => {
    const entries = selectedEntries.map((entry) => ({
      entry: entry,
      saleEnabled: entry.isSaleEnabled,
      packCode: entry.packCode,
    }));
    if (!(await dispatch(bulkAddFromWishlistToCartThunk(entries)))) {
      return;
    }

    const state = getState();
    const wishlistState = state.wishlistPage;
    const wishlist = wishlistState.wishlists[wishlistState.selectedWishlistId!];
    const allEntries = wishlist.entryIds.map((id) => wishlistState.entries[id]);
    sendWishlistAddSelectedToCartClick(
      selectedEntries.map((selectedEntry) => ({
        product: selectedEntry.product.base,
        index: allEntries.findIndex((entry) => entry.productCode === selectedEntry.product.base.code),
      })),
    );
  };
}

function bulkAddFromWishlistToCartThunk(
  entries: { entry: ResolvedWishlistEntryModel; saleEnabled: boolean; packCode: string }[],
): ThunkResult<Promise<boolean>> {
  return async (dispatch, getState) => {
    if (!entries.length) {
      return false;
    }

    const isAllLoaded = entries.every((entry) => entry.entry.product.isLoaded);

    const isCustomer = userIsCustomer();

    if (isCustomer && !isAllLoaded) {
      dispatch(setAddAllClicked(true));
      return false;
    }

    let state = getState();

    let needsAdditionalPnA = false;
    const promises = entries
      .map((entry) => {
        const salesData = calculateSalesCampaign4PDP({ productDescriptor: entry.entry.product });
        if (salesData.data?.hasSales && !!entry.entry.product.priceReference) {
          // There is a sale, BUT we have a price reference - we potentially need to download no price reference variant of PriceInfo
          needsAdditionalPnA = true;
          return ProductService.getProductDescriptor(entry.entry.product.base.code, "", { fetch: true }).promise;
        } else {
          return null;
        }
      })
      .filter(isNonNull);

    if (needsAdditionalPnA) {
      dispatch({ type: SET_ADD_ALL_CLICKED, payload: true });
      await Promise.all(promises);
      dispatch({ type: SET_ADD_ALL_CLICKED, payload: false });
      state = getState();
    }
    const apiData = entries.map((entry) => getATCApiEntry(entry.entry, state, entry.packCode, entry.saleEnabled));

    try {
      await sendBulkAddToCart(apiData);
      batch(() => {
        apiData.forEach((entry) => {
          dispatch(addQuantity(entry.productCode, entry.packageCode, entry.quantity, entry.priceReference));
        });
      });
      // const totalQty = entries.reduce((sum, entry) => sum + (entry.quantity || 0), 0);
      // const keyPart = userHasPermission(Permission.ADD_TO_QUOTE) ? "addedToQuote" : "addedToCart";

      openModal("add-to-cart-dialog", (close) => (
        <CartSuccessModal formData={apiData} onClose={close} page="WISHLIST" />
      ));

      // dispatch(
      //   addMessageAdvanced("info", {
      //     type: TranslationType.INTERNAL,
      //     value: `wishlistPage.globalMsg.${keyPart}`,
      //     params: { count: totalQty },
      //   }),
      // );
      return true;
    } catch {
      return false;
    }
  };
}

export function addToWishlist(data: {
  id?: string;
  wishlistName?: string;
  productCode: string;
  quantity: number;
  priceReference?: string;
  optionalParts?: {
    productCode: string;
    quantity: number;
  }[];
}): ThunkResult<Promise<FetchWishlistsResponse>> {
  return (dispatch) => {
    return sendDataWishlist(data).then(() => dispatch(fetchWishlistNames(true)));
  };
}

export function addCartToWishlist(
  data: { wishlistId: string } | { wishlistName: string },
): ThunkResult<Promise<string>> {
  return async (dispatch) => {
    let dataToSend;
    if ("wishlistId" in data) {
      dataToSend = {
        id: data.wishlistId,
      };
    } else {
      dataToSend = {
        wishlistName: data.wishlistName,
      };
    }

    // wishlist name is a required field for some reason
    await sendAddCartToWishlist(dataToSend);
    const result = await dispatch(fetchWishlistNames(true));
    if ("wishlistId" in data) {
      const wishlistItem = result.wishlists.filter((item) => item.id === data.wishlistId)[0];
      return wishlistItem ? wishlistItem.name : "";
    } else {
      return data.wishlistName;
    }
  };
}

export function bulkAddToWishlistThunk(
  wishlistData: { wishlistId: string } | { wishlistName: string },
  items: { code: string; quantity: number }[],
): ThunkResult<Promise<string>> {
  return async (dispatch, getState) => {
    const apiData = {
      wishlistId: "wishlistId" in wishlistData ? wishlistData.wishlistId : undefined,
      wishlistName:
        "wishlistName" in wishlistData
          ? wishlistData.wishlistName
          : getState().wishlistPage.wishlists[wishlistData.wishlistId].name,
      products: items,
    };
    await bulkAddToWishlist(apiData);
    const result = await dispatch(fetchWishlistNames(true));
    if ("wishlistId" in wishlistData) {
      const wishlistItem = result.wishlists.filter((item) => item.id === wishlistData.wishlistId)[0];
      return wishlistItem ? wishlistItem.name : "";
    } else {
      return wishlistData.wishlistName;
    }
  };
}

export function addOrderDetailsToWishlist(
  wishlistData: { wishlistId: string } | { wishlistName: string },
  order: OrderLineItemData[],
): ThunkResult<Promise<string>> {
  const qtyPerCode = {} as Record<string, number>;
  order.forEach((item) => (qtyPerCode[item.product.code] = item.qty + (qtyPerCode[item.product.code] || 0)));

  const items = Object.entries(qtyPerCode).map((data) => ({
    code: data[0],
    quantity: data[1],
  }));

  return bulkAddToWishlistThunk(wishlistData, items);
}

export function renameWishlist(wishlistId: string, newName: string): ThunkResult<Promise<unknown>> {
  return (dispatch, getState) => {
    const wishlist = getState().wishlistPage.wishlists[wishlistId];
    const description = wishlist.comment;
    return renameWishlistApi({ id: wishlistId, newName, description }).then(() =>
      dispatch({
        type: RENAME_WISHLIST,
        payload: {
          wishlistId,
          newName,
        },
      }),
    );
  };
}

export function createWishlistThunk(name: string): ThunkResult<Promise<FetchWishlistsResponse>> {
  return async (dispatch) => {
    await createWishlistApi({ wishlistName: name });
    const wishlistsData = await dispatch(fetchWishlistNames(true));
    dispatch(
      addMessageAdvanced("info", {
        type: TranslationType.INTERNAL,
        value: "wishlistPage.globalMsg.created",
        params: { name },
      }),
    );
    return wishlistsData;
  };
}

export function wishlistAfterScroll(oldLastSeen: number, newLastSeen: number): ThunkResult<void> {
  return (dispatch, getState) => {
    // We didn't see anything new - no impressions to send
    if (oldLastSeen >= newLastSeen) {
      return;
    }

    const state = getState();
    const basicProducts = state.products.basic;

    const wishlist = state.wishlistPage.wishlists[state.wishlistPage.selectedWishlistId!];
    const wishlistEntries = wishlist.entryIds.map((id) => state.wishlistPage.entries[id]);

    const entriesToSend = wishlistEntries.slice(oldLastSeen, newLastSeen);

    const impressions = entriesToSend.map((entry, index) => {
      const product = basicProducts[entry.productCode];
      const impression = convertProductToImpression({ basic: product });
      impression.list = "Wishlist";
      impression.position = 1 + oldLastSeen + index;
      return impression;
    });

    sendWishlistProductListImpression(impressions);
  };
}

export function quickEntryToCurrentWishlistThunk(
  data: {
    code: string;
    quantity: number;
  }[],
): ThunkResult<Promise<unknown>> {
  return async (dispatch, getState) => {
    const state = getState();
    const wishlistId = state.wishlistPage.selectedWishlistId!;
    const wishlistName = state.wishlistPage.wishlists[wishlistId].name;

    const apiData = {
      wishlistId,
      wishlistName,
      products: data,
    };
    await bulkAddToWishlist(apiData);
    await dispatch(fetchWishlistNames(true));
    return await dispatch(selectWishlistThunk(wishlistId));
  };
}
